import React, { useState, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { UtensilsCrossed, Search } from "lucide-react";
import DishCard from "./DishCard";

/**
 * Grid for dishes (Recetas / Bebidas). Handles single-select state so the
 * selected card shows its expanded CTA panel. Supports both the main view
 * (Ver / Archivar) and the archived view (Restaurar / Eliminar).
 *
 * Responsive columns: 2 → 3 → 4 → 5 → 6 → 7 across base/sm/md/lg/xl/2xl.
 */
const DishesGrid = React.memo(function DishesGrid({
  dishes,
  filteredDishes,
  searchQuery,
  onViewRecipe,
  onArchive,
  onRestore,
  onDelete,
  onClearFilters,
  dishesWithRecipesSet = null,
  isArchivedView = false,
  emptyTitle = "Sin platos",
  emptyDescription = "Primero añade platos a tus menús para poder crear recetas",
  busyId = null,
}) {
  const [selectedId, setSelectedId] = useState(null);

  const handleSelect = useCallback((dish) => {
    setSelectedId((prev) => (prev === dish.dish_id ? null : dish.dish_id));
  }, []);

  if (dishes.length === 0) {
    return (
      <Card className="border-dashed border-2">
        <CardContent className="py-16 text-center">
          <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-heading text-lg font-semibold mb-2">{emptyTitle}</h3>
          <p className="text-muted-foreground mb-6">{emptyDescription}</p>
        </CardContent>
      </Card>
    );
  }
  if (filteredDishes.length === 0) {
    return (
      <Card className="border-dashed border-2">
        <CardContent className="py-16 text-center">
          <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-heading text-lg font-semibold mb-2">Sin resultados</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery
              ? "No se encontraron elementos que coincidan con tu búsqueda"
              : "Sin elementos para mostrar"}
          </p>
          {onClearFilters && (
            <Button variant="outline" onClick={onClearFilters}>
              Limpiar filtros
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  return (
    <div
      className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7"
      data-testid="dishes-grid"
    >
      {filteredDishes.map((dish) => (
        <DishCard
          key={dish.dish_id}
          dish={dish}
          hasRecipe={dishesWithRecipesSet ? dishesWithRecipesSet.has(dish.dish_id) : null}
          isSelected={selectedId === dish.dish_id}
          onSelect={handleSelect}
          onView={onViewRecipe}
          onArchive={onArchive}
          onRestore={onRestore}
          onDelete={onDelete}
          isArchivedView={isArchivedView}
          busy={busyId === dish.dish_id}
        />
      ))}
    </div>
  );
});

export default DishesGrid;
